import React, { useCallback, useEffect, useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  myExpariencesService,
  EntityService,
  modelsManagementService,
} from "../../services";
import {
  Box,
  CircularProgress,
  Container,
  Button,
  Backdrop,
  IconButton,
  SvgIcon,
  Portal,
} from "@mui/material";
import { Typography } from "@mui/material";
import { ReactComponent as Verified } from "../../assets/verified_black.svg";
import { useTheme } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useTranslation } from "react-i18next";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { useDebounce, useViewport } from "../../helpers";
import { useSnackbar } from "notistack";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExparienceInformations from "./informations/ExparienceInformations";
import ExparienceSecurity from "./geolocalisation/ExparienceSecurity";
import UsersManagement from "./usersManagement/UsersManagement";
import ExparienceWebVR from "./webVR/ExparienceWebVR";
import Watermark from "./watermark/Watermark";
import InfoIcon from "@mui/icons-material/Info";
import SaveIcon from "@mui/icons-material/Save";
import SecurityIcon from "@mui/icons-material/Security";
import { NotificationsActive, School } from "@mui/icons-material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import SpeedDial from "@mui/material/SpeedDial";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import ConfirmationDialog from "../../components/dialog/ConfirmationDialog";
import LanguageIcon from "@mui/icons-material/Language";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import Survey from "../../components/survey/Survey";
import MyExparienceStats from "./statistics/MyExparienceStats";
import { resetExparience } from "../../reducers/expariences/expariencesSlice";
import {
  fetchSingleExparience,
  updatePublication,
  updateVisible,
} from "../../reducers/expariences/expariencesThunks";
import Configurator from "../../components/configurator/Configurator";
import {
  reinitializeConfig,
  setItemsInGrid,
  update3dView,
  updateLayout,
  updateSelectedTile,
} from "../../reducers/configurator/configuratorSlice";
import BarChartIcon from "@mui/icons-material/BarChart";
import useLongPress from "../../helpers/useLongPress";
import ResponsiveDialogManager from "../../components/dialog/ResponsiveDialogManager";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import GameLeaderboards from "./statistics/GameLeaderboards";
import {
  gamesKeys,
  sortOrder,
} from "../../components/configurator/utils/utils";
import { expariencesSocket } from "../../socket";
import PersonIcon from "@mui/icons-material/Person";
import SlotCounter from "react-slot-counter";
import ExparienceParticipants from "../../components/participants/ExparienceParticipants";
import Classes from "./classes/Classes";

const PREFIX = "MyExparienceManager";

const classes = {
  speedDial: `${PREFIX}-speedDial`,
  speedDialWithTileDialog: `${PREFIX}-speedDialWithTileDialog`,
  userCounter: `${PREFIX}-userCounter`,
  subheader: `${PREFIX}-subheader`,
  topButtonActions: `${PREFIX}-topButtonActions`,
  goBackButtonMargin: `${PREFIX}-goBackButtonMargin`,
  goBackButtonText: `${PREFIX}-goBackButtonText`,
  notifyTopButton: `${PREFIX}-notifyTopButton`,
  statsTopButton: `${PREFIX}-statsTopButton`,
  marginIcon: `${PREFIX}-marginIcon`,
  exparienceTitle: `${PREFIX}-exparienceTitle`,
  backdrop: `${PREFIX}-backdrop`,
  loaderContainer: `${PREFIX}-loaderContainer`,
  loadingIcon: `${PREFIX}-loadingIcon`,
  saveIcon: `${PREFIX}-saveIcon`,
  homeButton: `${PREFIX}-homeButton`,
  titleContainerPosition: `${PREFIX}-titleContainerPosition`,
  topButtonsContainer: `${PREFIX}-topButtonsContainer`,
  notPublishedSnackbar: `${PREFIX}-notPublishedSnackbar`,
  snackbarContentContainer: `${PREFIX}-snackbarContentContainer`,
  snackbarTitle: `${PREFIX}-snackbarTitle`,
  snackbarPublishButton: `${PREFIX}-snackbarPublishButton`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.speedDial}`]: {
    position: "fixed",
    "&.MuiSpeedDial-directionUp, &.MuiSpeedDial-directionLeft": {
      bottom: theme.spacing(2),
      right: theme.spacing(2),
    },
    "&.MuiSpeedDial-directionDown, &.MuiSpeedDial-directionRight": {
      top: theme.spacing(2),
      left: theme.spacing(2),
    },
  },

  [`& .${classes.speedDialWithTileDialog}`]: {
    position: "fixed",
    "&.MuiSpeedDial-directionUp, &.MuiSpeedDial-directionLeft": {
      bottom: "360px",
      right: theme.spacing(2),
    },
    "&.MuiSpeedDial-directionDown, &.MuiSpeedDial-directionRight": {
      top: theme.spacing(2),
      left: theme.spacing(2),
    },
  },

  [`& .${classes.userCounter}`]: {
    backgroundColor: theme.palette.primary.main,
    color: "#000000",
    position: "fixed",
    bottom: "1.5rem",
    left: "1rem",
    zIndex: 999,
    padding: "0.5rem",
    borderRadius: "10px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    cursor: "pointer",
  },

  [`& .${classes.subheader}`]: {
    zIndex: 999,
    width: "100%",
    position: "sticky",
    backgroundColor: "#303030",
    top: 0,
    paddingTop: "12px",
  },

  [`& .${classes.topButtonActions}`]: {
    display: "flex",
    maxHeight: "56px",
  },

  [`& .${classes.goBackButtonMargin}`]: {
    margin: "1rem 1rem 0 1rem",
  },

  [`& .${classes.goBackButtonText}`]: {
    fontSize: "14px",
  },

  [`& .${classes.notifyTopButton}`]: {
    margin: "1rem 0 0 0",
    marginRight: "1rem",
  },

  [`& .${classes.statsTopButton}`]: {
    margin: "1rem 0 0 0",
  },

  [`& .${classes.marginIcon}`]: {
    marginRight: "0.5rem",
  },

  [`& .${classes.exparienceTitle}`]: {
    textAlign: "center",
    margin: "2rem 0 1rem 0 !important",
    maxWidth: "70vw",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  [`& .${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },

  [`& .${classes.loaderContainer}`]: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#484848",
    padding: "1rem",
    borderRadius: "12px",
  },

  [`& .${classes.loadingIcon}`]: {
    margin: "1rem auto",
  },

  [`& .${classes.saveIcon}`]: {
    height: "28px",
  },

  [`& .${classes.homeButton}`]: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "50%",
    height: "45px",
    width: "45px",
    transform: "translateY(20px)",
    marginLeft: "1rem",
  },

  [`& .${classes.titleContainerPosition}`]: {
    display: "flex",
    justifyContent: "center",
  },

  [`& .${classes.topButtonsContainer}`]: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
  },

  [`& .${classes.notPublishedSnackbar}`]: {
    height: "40px",
    width: "100%",
    backgroundColor: theme.palette.primary.main,
  },

  [`& .${classes.snackbarContentContainer}`]: {
    maxWidth: "650px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    margin: "0 auto",
  },

  [`& .${classes.snackbarTitle}`]: {
    transform: "translateY(10px)",
    color: "#484848",
  },

  [`& .${classes.snackbarPublishButton}`]: {
    height: "30px",
    marginTop: "5px",
    border: "1px solid #FFFFFFB3",
  },
}));

const ITEM_HEIGHT = 48;

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  const { width } = useViewport();

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      key={index + 1}
      {...other}
    >
      {value === index && (
        <Box sx={{ padding: width < 769 ? "12px" : "24px" }}>{children}</Box>
      )}
    </div>
  );
}

export default function MyExparienceManager(props) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const history = useNavigate();
  const { id, section } = useParams();
  const { width } = useViewport();

  const [exparience, setExparience] = useState(null);
  const [datesDisable, setDatesDisable] = useState(false);
  const [files, setFiles] = useState({});
  const [fetchedEntities, setFetchedEntities] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveConfigurator, setSaveConfigurator] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [noReloadImages, setNoReloadImages] = useState(false);
  const [name, setName] = useState("");
  const [isNameSearching, setIsNameSearching] = useState(false);
  const [isNameUsed, setIsNameUsed] = useState(null);
  const [frames, setFrames] = useState([]);
  const [visibleFrames, setVisibleFrames] = useState([]);
  const [offerings, setOfferings] = useState(null);
  const [confirmSave, setConfirmSave] = useState({ open: false, index: 0 });
  const [configObjects, setConfigObjects] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [gameLeaderboards, setGameLeaderboards] = useState(false);

  const [exparienceSocketData, setExparienceSocketData] = useState({
    participants: 0,
    logins: [],
    openDetail: false,
  });

  const participantsDetailsRef = useRef(false);

  const open = Boolean(anchorEl);
  const debouncedSearchName = useDebounce(name, 1000);

  const isSuperAdmin = useSelector(
    (state) => state.user.currentUser?.account?.isSuperAdmin
  );
  const needSave = useSelector((state) => state.configurator.needSave);
  const configuratorGrid = useSelector((state) => state.configurator.grid);
  const parentedLayoutsList = useSelector(
    (state) => state.configurator.parentedLayoutsList
  );
  const selectedExparience = useSelector(
    (state) => state.myExpariences.selectedExparience
  );
  const [index, setIndex] = useState(
    location.state && location.state.index ? location.state.index : 0
  );
  const saveNeeded = useSelector((state) => state.configurator.needSave);
  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );
  const is3dView = useSelector((state) => state.configurator.is3dView);
  const defaultGrid = useSelector((state) => state.configurator.grid.default);
  const layout = useSelector((state) => state.configurator.layout);
  // const grid = useSelector((state) => state.configurator.grid);
  const layoutsList = useSelector((state) => state.configurator.layouts);

  const userToken = useSelector((state) => state.user?.currentUser?.token);
  const pseudo = useSelector((state) => state.user?.account?.pseudo);

  const currentTile = useSelector((state) => state.configurator?.selectedTile);

  const getJsonForUnity = () => {
    if (isAdmin) {
      myExpariencesService
        .fetchUnityFinalJson(id)
        .then((res) => {
          const tmp = { ...res };
          tmp.backendUrl = process.env.REACT_APP_GATEWAY_URL;
          tmp.token = userToken;
          tmp.pseudo = pseudo;
          navigator.clipboard.writeText(JSON.stringify(tmp));
          enqueueSnackbar(t("toasts.jsonForUnityCopied"), {
            variant: "success",
          });
        })
        .catch((err) => {
          enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        });
    } else console.log("nope");
  };

  const nameLongPress = useLongPress(getJsonForUnity, 2000);

  useEffect(() => {
    if (
      debouncedSearchName &&
      debouncedSearchName.length > 0 &&
      selectedExparience?.name !== name
    ) {
      setIsNameSearching(true);
      myExpariencesService
        .isExparienceNameExist(debouncedSearchName.trim())
        .then((results) => {
          setIsNameSearching(false);
          setIsNameUsed(results.exists);
        });
    }
  }, [debouncedSearchName]);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenStatsModal = () => {
    setShowStatsModal(true);
  };

  const handleCloseStatsModal = () => {
    setShowStatsModal(false);
  };

  const getSingleExparience = async (id) => {
    try {
      const thunk = await dispatch(fetchSingleExparience({ id, isAdmin }));
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
    } catch {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  useEffect(() => {
    dispatch(reinitializeConfig());
    getSingleExparience(id);

    expariencesSocket.connect();
    expariencesSocket.emit("subscribe", { exparienceId: id }, () =>
      enqueueSnackbar(t("toasts.socketTimeout"), { variant: "error" })
    );
    expariencesSocket.on("participants", (res) => {
      setExparienceSocketData({
        openDetail: participantsDetailsRef.current,
        participants: res.size,
        logins: res.logins,
      });
    });

    return () => {
      expariencesSocket.off("participants");
      expariencesSocket.disconnect();
    };
  }, [id]);

  useEffect(() => {
    if (section) {
      switch (section) {
        case "configurator":
          setIndex(1);
          break;
        default:
          setIndex(0);
          break;
      }
    }
  }, [section]);

  useEffect(() => {
    if (selectedExparience) {
      setExparience(selectedExparience);
      setFrames([
        ...selectedExparience.objects.filter(
          (obj) => obj.type === "frame" || obj.type === "custom"
        ),
      ]);
      // in case old expariences contains forbidden caracters
      let tmpName = selectedExparience.name
        .replace(/[&\/\\#,+()$~%._'":*?<>{} ]/g, "-")
        .replace(/éèêë/g, "e")
        .replace(/àâ/g, "a")
        .replace(/ï/g, "i");
      setName(tmpName);
    }
  }, [selectedExparience]);

  useEffect(() => {
    if (exparience) {
      if (
        exparience.objects.length > 0 ||
        exparience.themes.length > 0 ||
        exparience.walls.length > 0
      )
        dispatch(
          setItemsInGrid({
            layouts: exparience.layouts,
            rowNumber: exparience.rowNumber,
            columnNumber: exparience.columnNumber,
            objects: exparience.objects,
            themes: exparience.themes,
            walls: exparience.walls,
            noReload: noReloadImages,
          })
        );
      setFrames([
        ...exparience.objects.filter(
          (obj) => obj.type === "frame" || obj.type === "custom"
        ),
      ]);
    }
  }, [exparience]);

  useEffect(() => {
    if (exparience) {
      const myObjects = exparience.webXrOrder
        ? exparience.objects.filter((item) =>
            exparience.webXrOrder.includes(item._id)
          )
        : exparience.objects;
      const tmp = myObjects.filter(
        (obj) =>
          (obj.type === "frame" && obj.visible && obj.imageUrl) ||
          (obj.type === "custom" && obj.visible && (obj.imageUrl || obj.link))
      );
      if (exparience.webXrOrder)
        tmp.sort((a, b) => {
          return (
            exparience.webXrOrder.indexOf(a._id) -
            exparience.webXrOrder.indexOf(b._id)
          );
        });
      setVisibleFrames(tmp);
    }
  }, [frames, exparience]);

  useEffect(() => {
    dispatch(updateLayout("normal"));
    if (!fetchedEntities && isAdmin) {
      EntityService.getEntities("sphere")
        .then((result) => {
          setFetchedEntities(result.data);
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("toasts.getEntitesError"), { variant: "error" });
        });
    }
    if (!offerings) {
      myExpariencesService.getOfferings().then(
        (res) => {
          setOfferings(res);
        },
        (error) => {
          enqueueSnackbar(t("toasts.getOfferingsError"), { variant: "error" });
        }
      );
    }
    modelsManagementService
      .getAllConfigModels(isAdmin)
      .then((res) => setConfigObjects(res.data))
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.getConfiguratorObjectsError"), {
            variant: "error",
          });
      });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [index]);

  function handleChange(e, value) {
    if (is3dView) dispatch(update3dView(false));
    if (index === 1 && saveNeeded) {
      setConfirmSave({ open: true, index: value });
    } else {
      setIndex(value);
    }
  }

  function a11yProps(index) {
    return {
      id: `scrollable-prevent-tab-${index}`,
      "aria-controls": `scrollable-prevent-tabpanel-${index}`,
    };
  }

  const getPeer = (side) => {
    switch (side) {
      case "north":
        return "top";
      case "east":
        return "right";
      case "south":
        return "bottom";
      case "west":
        return "left";
      default:
        return "";
    }
  };

  const getDirectionByDegrees = (degrees) => {
    switch (degrees) {
      case 0:
        return "north";
      case 90:
        return "east";
      case 180:
        return "south";
      case 270:
        return "west";
      default:
        return "north";
    }
  };

  const formatConfiguratorObjects = () => {
    let tmp = [];
    const files = [];
    let croppedFiles = [];
    let entryPoint = null;
    const myThemes = { themes: [], files: [] };
    const myWalls = [];

    Object.keys(configuratorGrid).forEach((myLayout) =>
      configuratorGrid[myLayout].forEach((row) => {
        row.forEach((tile) => {
          const keys = Object.keys(tile).filter(
            (key) => !["row", "column"].includes(key)
          );
          keys.forEach((key) => {
            const parentKey = Object.keys(parentedLayoutsList).find((opt) =>
              parentedLayoutsList[opt].includes(myLayout)
            );
            if (tile[key].type) {
              const {
                startTile,
                endTile,
                peer,
                file,
                isLoaded,
                searchTargets,
                searchContainer,
                ...data
              } = tile[key];
              if (
                (startTile &&
                  endTile &&
                  startTile.row === tile.row &&
                  startTile.column === tile.column) ||
                !endTile
              ) {
                if (tile[key].type === "spawn" && !tile[key].offset)
                  delete data.offset;

                if (tile[key].type === "ipad") {
                  data.type = "socle";
                  data.contentType = "ipad";
                }

                if (tile[key].type === "screen" && data.contentType) {
                  delete data.contentType;
                }

                if (file) {
                  files.push(file);
                  data.image = file.name;
                }

                if (
                  peer &&
                  tile[key].type !== "ipad" &&
                  (startTile.row !== endTile.row ||
                    startTile.column !== endTile.column)
                )
                  data.peer = getPeer(peer.start);

                if (tile[key].type === "logo")
                  data.rotation = getDirectionByDegrees(tile[key].rotation);

                if (tile[key].type === "guest")
                  data.guests = tile[key].guests.map((guest) => guest.id);

                data.row = startTile && endTile ? startTile.row : tile.row;
                data.column =
                  startTile && endTile ? startTile.column : tile.column;
                if (endTile) {
                  data.rowEnd = endTile.row;
                  data.columnEnd = endTile.column;
                }

                if (data.externalLinks?.length > 0) {
                  croppedFiles.push({
                    id: data.id || null,
                    externalLinks: [...data.externalLinks],
                  });
                  let finalExternals = [];
                  data.externalLinks.forEach((elem) => {
                    const { fileName, ...left } = elem;
                    finalExternals.push({
                      ...left,
                      logo: fileName ? fileName : elem.logo,
                    });
                  });
                  data.externalLinks = finalExternals;
                }
                const finaldata = { ...data, layout: myLayout };
                if (parentKey) finaldata.parentLayout = parentKey;
                // console.log("finalData =>", finaldata);
                tmp.push(finaldata);
              }
            } else if (
              tile[key].radius &&
              (tile[key].theme || tile[key].song)
            ) {
              const myTheme = {
                row: tile.row,
                column: tile.column,
                radius: tile[key].radius,
                title: tile[key].title || "",
                description: tile[key].description || "",
                theme: tile[key].theme || "",
                layout: myLayout,
              };
              if (tile[key].areaMode === "scaled") {
                myTheme.scaleX = tile[key].scaleX || 1;
                myTheme.scaleY = tile[key].scaleY || 1;
                if (tile[key].offsetX) myTheme.offsetX = tile[key].offsetX;
                if (tile[key].offsetY) myTheme.offsetY = tile[key].offsetY;
              }
              if (parentKey) myTheme.parentLayout = parentKey;
              if (tile[key].song) {
                myTheme.loop = tile[key].loop;
                if (
                  typeof tile[key].song === "string" ||
                  tile[key].song instanceof String
                ) {
                  //song is not a file but url
                  myTheme.ambiantMusic = tile[key].song;
                } else {
                  //song is a file
                  myTheme.ambiantMusic = tile[key].song[0].name.replaceAll(
                    " ",
                    "-"
                  );
                  myThemes.files.push(tile[key].song[0]);
                }
              }
              if (myTheme.ambiantMusic || myTheme.filter || myTheme.theme)
                myThemes.themes.push(myTheme);
            } else if (key === "walls") {
              if (
                tile[key].north.color ||
                tile[key].east.color ||
                tile[key].west.color ||
                tile[key].south.color
              ) {
                let tmp = {
                  row: tile.row,
                  column: tile.column,
                  layout: myLayout,
                  north: tile[key].north.color || null,
                  east: tile[key].east.color || null,
                  west: tile[key].west.color || null,
                  south: tile[key].south.color || null,
                };
                if (parentKey) tmp.parentLayout = parentKey;
                myWalls.push(tmp);
              }
            }
          });
        });
      })
    );

    return {
      objects: tmp,
      files: getUniqueFiles(files),
      entryPoint,
      croppedFiles,
      tmpThemes: { ...myThemes, files: getUniqueFiles(myThemes.files) },
      walls: [...myWalls],
    };
  };

  const getUniqueFiles = (files) => {
    const uniqueFiles = files.length > 0 ? [files[0]] : [];
    files.forEach((file) => {
      let exist = false;
      uniqueFiles.forEach((uniq) => {
        if (uniq.name === file.name) exist = true;
      });
      if (!exist) {
        uniqueFiles.push(file);
      }
    });
    return uniqueFiles;
  };

  const handleAsyncExternalLinks = (tmpObjects, tmpExparience) => {
    const promises = [];
    if (tmpObjects.croppedFiles?.length > 0) {
      tmpObjects.croppedFiles.forEach((fullElem) => {
        let formatedCropped = [];
        let logos = [];
        fullElem.externalLinks.forEach((externalLink) => {
          if (externalLink.fileName)
            logos.push({
              file: externalLink.logo,
              fileName: externalLink.fileName,
            });
          formatedCropped.push({
            logo: externalLink.fileName
              ? externalLink.fileName
              : externalLink.logo,
            link: externalLink.link,
            rapidAccess: externalLink.rapidAccess,
            title: externalLink.title,
          });
        });
        promises.push(
          myExpariencesService.saveExparienceExternalLinks(
            tmpExparience._id,
            fullElem.id,
            formatedCropped,
            logos
          )
        );
      });
    }
    return Promise.all(promises);
  };

  const handleAsyncThemes = async (tmpObjects, tmpExparience) => {
    const themeList = await myExpariencesService
      .uploadConfiguratorThemes(
        tmpObjects.tmpThemes.themes,
        tmpObjects.tmpThemes.files,
        tmpExparience._id,
        isAdmin
      )
      .then(
        (returnedThemes) => {
          tmpExparience.themes = [...returnedThemes];
          return tmpExparience;
        },
        (error) => {
          enqueueSnackbar(t("toasts.uploadThemesError"), { variant: "error" });
          return tmpExparience;
        }
      );
    return themeList;
  };

  const handleAsyncCalls = async (tmpObjects, tmpExparience) => {
    const tmpCustomObjects = [...tmpExparience.objects];
    await handleAsyncExternalLinks(tmpObjects, tmpExparience)
      .then((values) => {
        values.forEach((item) => {
          const index = tmpCustomObjects.findIndex((subItem) => {
            return subItem._id === item._id;
          });
          if (index >= 0) tmpCustomObjects.splice(index, 1, item);
          else tmpCustomObjects.push(item);
        });
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.updateExternalLinksError"), {
            variant: "error",
          });
      });
    tmpExparience = { ...tmpExparience, objects: tmpCustomObjects };
    tmpExparience = await handleAsyncThemes(tmpObjects, tmpExparience);
    tmpExparience = { ...tmpExparience, walls: tmpObjects.walls };
    return tmpExparience;
  };

  const matchCroppedWithId = (tmpObjects, obj) => {
    let tmp = { ...tmpObjects };
    tmp.croppedFiles.forEach((elem, index) => {
      if (!elem.id) {
        elem.externalLinks.forEach((subElem) => {
          obj.forEach((item) => {
            const found = item.externalLinks.find((subItem) => {
              return subItem.logo === subElem.fileName;
            });
            if (found) tmp.croppedFiles[index].id = item._id;
          });
        });
      }
    });
    return tmp;
  };

  const handleSaveExparience = async (callback) => {
    dispatch(updateSelectedTile(null));
    let tmpExparience = { ...exparience };
    // remove the checkbox needed for handling geoloc visible by date for the backend
    const tmpPositions = tmpExparience.positions.map((item) => {
      const { controlDate, ...rest } = item.position;
      return {
        ...item,
        position: {
          ...rest,
          startDate: item.position.startDate || null,
          endDate: item.position.endDate || null,
        },
      };
    });
    tmpExparience.positions = tmpPositions;

    // tmpExparience.groups = [];

    tmpExparience.name = name;

    tmpExparience.rowNumber = defaultGrid.length;
    tmpExparience.columnNumber = defaultGrid[0].length;
    tmpExparience.layouts = layoutsList;

    let hasConfig = false;
    let tmpObjects;
    if (needSave) {
      hasConfig = true;
      setSaveConfigurator(true);
      tmpObjects = await formatConfiguratorObjects();
      console.log("objects to send =>", tmpObjects, tmpExparience);
      setNoReloadImages(true);
      myExpariencesService
        .updateConfigurator(
          tmpObjects.objects,
          tmpObjects.files,
          tmpExparience._id,
          isAdmin
        )
        .then(
          async (res) => {
            enqueueSnackbar(t("toasts.exparienceUpdatedSuccess"), {
              variant: "success",
            });
            tmpObjects = await matchCroppedWithId(tmpObjects, res);
            tmpExparience = await handleAsyncCalls(tmpObjects, tmpExparience);
            await updateExparience(tmpExparience, res, hasConfig, callback);
            setSaveConfigurator(false);
          },
          (error) => {
            setSaveConfigurator(false);
            enqueueSnackbar(t("toasts.exparienceConfigUpdatedError"), {
              variant: "error",
            });
          }
        );
    } else {
      setSaving(true);
      updateExparience(tmpExparience, null, hasConfig);
    }
  };

  const updateExparience = (tmpExparience, res, hasConfig, callback) => {
    if (res) {
      const tmp = [];
      res.forEach((item) => {
        const found = tmpExparience.objects.find(
          (elem) => elem._id === item._id
        );
        if (found && found.externalLinks?.length > 0)
          tmp.push({ ...item, externalLinks: [...found.externalLinks] });
        else tmp.push(item);
      });
      tmpExparience.objects = tmp;
    }
    let tmpFiles = [];
    const keys = Object.keys(files);
    if (keys.length > 0) {
      keys.forEach((fileKey) => {
        tmpFiles.push(files[fileKey][0]);
      });
    }
    tmpFiles = getUniqueFiles(tmpFiles);
    if (tmpExparience.tags && tmpExparience.tags.length > 0) {
      if (typeof tmpExparience.tags[0] !== "string") {
        const tmpTags = tmpExparience.tags?.map((item) => item.label);
        tmpExparience.tags = tmpTags;
      }
    }

    const idsToInclude = tmpExparience.objects
      .filter(
        (obj) =>
          (obj.type === "frame" && obj.visible && obj.imageUrl) ||
          (obj.type === "custom" && obj.visible && (obj.imageUrl || obj.link))
      )
      .map((item) => item._id)
      .filter((myId) => !exparience.webXrOrder?.includes(myId));
    const myArr = !exparience.webXrOrder ? [] : exparience.webXrOrder;
    tmpExparience.webXrOrder = [...myArr, ...idsToInclude];

    myExpariencesService
      .updateExparienceByRole(tmpExparience, tmpFiles, isAdmin)
      .then(
        () => {
          if (!hasConfig) {
            enqueueSnackbar(t("toasts.exparienceUpdatedSuccess"), {
              variant: "success",
            });
          }
          setExparience(tmpExparience);
          setSaving(false);
          if (callback) callback();
        },
        (error) => {
          enqueueSnackbar(t("toasts.exparienceUpdatedError"), {
            variant: "error",
          });
          setSaving(false);
        }
      );
  };

  const updateMyPublication = async (id, published, isAdmin, callback) => {
    try {
      const thunk = await dispatch(
        updatePublication({ id, value: published, isAdmin })
      );
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      callback();
      enqueueSnackbar(t("toasts.updatePublicationExparienceSuccess"), {
        variant: "success",
      });
    } catch {
      enqueueSnackbar(t("toasts.updatePublicationExparienceError"), {
        variant: "error",
      });
    }
  };

  const updateMyVisible = async (id, visible, isAdmin, callback) => {
    try {
      const thunk = await dispatch(updateVisible({ id, visible, isAdmin }));
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      callback();
      enqueueSnackbar(t("toasts.updatePublicationExparienceSuccess"), {
        variant: "success",
      });
    } catch {
      enqueueSnackbar(t("toasts.updatePublicationExparienceError"), {
        variant: "error",
      });
    }
  };

  const handleExparienceService = (mode) => {
    const actions = {
      publish: () => {
        updateMyPublication(exparience.id, !exparience.published, isAdmin, () =>
          setExparience({ ...exparience, published: !exparience.published })
        );
        setConfirmation("");
      },
      visible: () => {
        updateMyVisible(exparience._id, !exparience.visible, isAdmin, () =>
          setExparience({ ...exparience, visible: !exparience.visible })
        );
        setConfirmation("");
      },
      notify: () => {
        myExpariencesService
          .sendNotification(exparience._id, isAdmin)
          .then(() => {
            enqueueSnackbar(t("toasts.notificationSent"), {
              variant: "success",
            });
          })
          .catch((err) => {
            if (err === 403)
              enqueueSnackbar(t("toasts.unauthorizedError"), {
                variant: "error",
              });
            else
              enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
          });
        setConfirmation("");
      },
      duplicate: () => {
        setConfirmation("");
        myExpariencesService
          .duplicateExparience(
            exparience._id,
            () =>
              enqueueSnackbar(t("toasts.duplicateExparienceSuccess"), {
                variant: "success",
              }),
            () =>
              enqueueSnackbar(t("toasts.duplicateExparienceError"), {
                variant: "error",
              })
          )
          .then((res) => {
            history(`/myExpariences/${res.id}`);
          });
      },
      promote: () => {
        setConfirmation("");
        myExpariencesService
          .promoteExparience(
            exparience._id,
            () =>
              enqueueSnackbar(t("toasts.promoteExparienceSuccess"), {
                variant: "success",
              }),
            () =>
              enqueueSnackbar(t("toasts.promoteExparienceError"), {
                variant: "error",
              })
          )
          .then((res) => {
            setExparience(res);
          });
      },
      unpromote: () => {
        setConfirmation("");
        myExpariencesService
          .unPromoteExparience(
            exparience._id,
            () =>
              enqueueSnackbar(t("toasts.unPromoteExparienceSuccess"), {
                variant: "success",
              }),
            () =>
              enqueueSnackbar(t("toasts.unPromoteExparienceError"), {
                variant: "error",
              })
          )
          .then((res) => {
            setExparience(res);
          });
      },
    };
    return actions.hasOwnProperty(mode) ? actions[mode]() : () => undefined;
  };

  const checkContactEmailIsValid = () => {
    if (exparience)
      return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        exparience.contact
      );
    else return true;
  };

  const handleCloseParticipantsDetails = useCallback(() => {
    participantsDetailsRef.current = false;
    setExparienceSocketData({ ...exparienceSocketData, openDetail: false });
  }, [exparienceSocketData]);

  return (
    <Root>
      <ExparienceParticipants
        open={exparienceSocketData.openDetail}
        handleClose={handleCloseParticipantsDetails}
        participants={exparienceSocketData.logins}
      />
      {index !== 5 && (
        <SpeedDial
          ariaLabel="Menu"
          className={
            index === 1
              ? currentTile || layout === "walls"
                ? classes.speedDialWithTileDialog
                : classes.speedDial
              : classes.speedDial
          }
          hidden={
            isNameUsed ||
            isNameSearching ||
            datesDisable ||
            // (exparience?.endDate && !exparience?.startDate) ||
            !checkContactEmailIsValid() ||
            /[&/\\#,+()$~%._'":*?<>{}é èêëàâ]/g.test(name)
          }
          open={true}
          icon={<SaveIcon />}
          onClick={async () => {
            await handleSaveExparience(() => {
              setConfirmSave({ open: false, index: 0 });
            });
          }}
        />
      )}
      <Box
        aria-label="UserCounter"
        className={classes.userCounter}
        onClick={() => {
          participantsDetailsRef.current = true;
          setExparienceSocketData({
            ...exparienceSocketData,
            openDetail: true,
          });
        }}
      >
        <SlotCounter value={exparienceSocketData.participants} />
        <PersonIcon sx={{ marginLeft: "0.25rem", color: "#000000b3" }} />
      </Box>
      {confirmation.length > 0 && (
        <ConfirmationDialog
          open={confirmation.length > 0 ? true : false}
          onAgree={() => handleExparienceService(confirmation)}
          setOpen={setConfirmation}
          mode={confirmation}
          isActive={
            confirmation === "publish"
              ? exparience.published
              : confirmation === "visible"
              ? exparience.visible
              : false
          }
        />
      )}
      {confirmSave && (
        <ConfirmationDialog
          open={confirmSave.open}
          onAgree={async () => {
            dispatch(update3dView(false));
            await handleSaveExparience(() => {
              setIndex(confirmSave.index);
              setConfirmSave({ open: false, index: 0 });
            });
          }}
          customClose={() => {
            dispatch(update3dView(false));
            setIndex(confirmSave.index);
            setConfirmSave({ open: false, index: 0 });
          }}
          setOpen={setConfirmSave}
          mode="customSave"
          element="Configuration"
        />
      )}
      <ResponsiveDialogManager
        open={gameLeaderboards}
        fullScreen
        title={"Leaderboards"}
        renderer={
          <GameLeaderboards
            exparienceId={id}
            gamesList={exparience?.objects
              .filter((obj) => gamesKeys.includes(obj.type))
              .sort(
                (current, next) =>
                  sortOrder.indexOf(current.type) - sortOrder.indexOf(next.type)
              )}
            exparienceName={exparience?.name}
            exparienceDisplayName={exparience?.displayName}
          />
        }
        onClose={() => setGameLeaderboards(false)}
      />
      <Backdrop open={saving || saveConfigurator} className={classes.backdrop}>
        <div className={classes.loaderContainer}>
          <CircularProgress
            size={50}
            color="primary"
            className={classes.loadingIcon}
          />
          <Typography variant="body1">{t("loaders.saving")}</Typography>
        </div>
      </Backdrop>
      <div className={classes.topButtonsContainer}>
        <Button
          variant="contained"
          color="primary"
          className={classes.goBackButtonMargin}
          onClick={() => {
            dispatch(resetExparience());
            history(`/myExpariences`);
          }}
        >
          <KeyboardReturnIcon className={classes.marginIcon} />
          {width >= 768 && (
            <span className={classes.goBackButtonText}>
              {t("topMenu.myExpariences")}
            </span>
          )}
        </Button>
        <div className={classes.topButtonActions}>
          <Button
            variant="contained"
            color="primary"
            className={classes.notifyTopButton}
            onClick={() => setConfirmation("notify")}
          >
            <NotificationsActive className={classes.saveIcon} />
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.statsTopButton}
            onClick={handleOpenStatsModal}
          >
            <BarChartIcon />
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.goBackButtonMargin}
            onClick={() => setGameLeaderboards(true)}
          >
            <SportsEsportsIcon className={classes.saveIcon} />
          </Button>
          {/* <Button
            variant="contained"
            color="primary"
            className={classes.goBackButtonMargin}
            disabled={
              isNameSearching ||
              isNameUsed ||
              datesDisable ||
              // (exparience?.endDate && !exparience?.startDate) ||
              !checkContactEmailIsValid() ||
              /[&\/\\#,+()$~%._'":*?<>{}é èêëàâ]/g.test(name)
            }
            onClick={() => handleSaveExparience()}
          >
            <SaveIcon className={classes.saveIcon} />
          </Button> */}
        </div>
      </div>
      {exparience && (
        <div className={classes.titleContainerPosition}>
          <Typography
            variant="h5"
            className={classes.exparienceTitle}
            {...nameLongPress}
          >
            {exparience.displayName
              ? exparience.displayName.toUpperCase()
              : exparience.name.toUpperCase()}
          </Typography>
          <React.Fragment>
            <IconButton
              aria-label="more"
              aria-controls="long-menu"
              aria-haspopup="true"
              color="primary"
              component="span"
              className={classes.homeButton}
              onClick={handleOpenMenu}
              size="large"
            >
              <MoreVertIcon style={{ color: "#000" }} />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={handleCloseMenu}
              slotProps={{
                paper: {
                  style: {
                    maxHeight: isAdmin ? ITEM_HEIGHT * 6.5 : ITEM_HEIGHT * 4.5,
                    marginTop: "-50px", // button size
                  },
                },
              }}
            >
              <MenuItem
                onClick={(e) => {
                  handleCloseMenu();
                  setConfirmation("publish");
                }}
              >
                <IconButton
                  className={classes.buttonSpaces}
                  color="primary"
                  size="large"
                >
                  {exparience.published ? (
                    <SvgIcon component={Verified} />
                  ) : (
                    <NewReleasesIcon />
                  )}
                </IconButton>
                <Typography variant="body1" style={{ color: "#FFFFFFB3" }}>
                  {exparience.published
                    ? t("myExpariences.unpublishButton")
                    : t("myExpariences.publishButton")}
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  handleCloseMenu();
                  setConfirmation("notify");
                }}
              >
                <IconButton
                  className={classes.buttonSpaces}
                  color="primary"
                  size="large"
                >
                  <NotificationsActive />
                </IconButton>
                <Typography variant="body1" style={{ color: "#FFFFFFB3" }}>
                  {t("myExpariences.notify")}
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  handleCloseMenu();
                  setConfirmation("visible");
                }}
              >
                <IconButton
                  className={classes.buttonSpaces}
                  color="primary"
                  size="large"
                >
                  {exparience.visible ? <Visibility /> : <VisibilityOff />}
                </IconButton>
                <Typography variant="body1" style={{ color: "#FFFFFFB3" }}>
                  {exparience.visible
                    ? t("myExpariences.makeInvisible")
                    : t("myExpariences.makeVisible")}
                </Typography>
              </MenuItem>
              {isAdmin && (
                <MenuItem
                  onClick={(e) => {
                    handleCloseMenu();
                    setConfirmation("duplicate");
                  }}
                >
                  <IconButton
                    className={classes.buttonSpaces}
                    color="primary"
                    size="large"
                  >
                    <FileCopyIcon />
                  </IconButton>
                  <Typography variant="body1" style={{ color: "#FFFFFFB3" }}>
                    {t("myExpariences.duplicateExparience")}
                  </Typography>
                </MenuItem>
              )}
              {isSuperAdmin && (
                <MenuItem
                  onClick={(e) => {
                    handleCloseMenu();
                    setConfirmation(
                      !exparience.promote ? "promote" : "unpromote"
                    );
                  }}
                >
                  <IconButton
                    className={classes.buttonSpaces}
                    color="primary"
                    size="large"
                  >
                    <FileCopyIcon />
                  </IconButton>
                  <Typography variant="body1" style={{ color: "#FFFFFFB3" }}>
                    {!exparience.promote
                      ? t("myExpariences.promoteExparience")
                      : t("myExpariences.unpromoteExparience")}
                  </Typography>
                </MenuItem>
              )}
            </Menu>
          </React.Fragment>
        </div>
      )}
      {exparience && !exparience.published && (
        <div className={classes.notPublishedSnackbar}>
          <div className={classes.snackbarContentContainer}>
            <Typography variant="body2" className={classes.snackbarTitle}>
              {t("myExpariences.unpublishedWarning")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.snackbarPublishButton}
              onClick={() => handleExparienceService("publish")}
            >
              {t("myExpariences.publish")}
            </Button>
          </div>
        </div>
      )}
      <div className={classes.subheader}>
        <Tabs
          value={index}
          onChange={handleChange}
          indicatorColor="primary"
          scrollButtons={width <= 520 ? true : "auto"}
          variant={width <= 520 ? "scrollable" : "fullWidth"}
          aria-label="full width tabs my design"
        >
          <Tab
            icon={<InfoIcon />}
            aria-label="informations"
            {...a11yProps(0)}
          />
          <Tab
            icon={<DashboardIcon />}
            aria-label="configurator"
            {...a11yProps(1)}
            disabled={false}
          />
          <Tab icon={<LanguageIcon />} aria-label="webVR" {...a11yProps(2)} />
          <Tab
            icon={<SecurityIcon />}
            aria-label="map"
            {...a11yProps(3)}
            disabled={false}
          />
          <Tab icon={<School />} aria-label="conversion" {...a11yProps(4)} />
          <Tab
            icon={<QuestionAnswerIcon />}
            aria-label="surveys"
            {...a11yProps(5)}
          />
        </Tabs>
      </div>
      {exparience ? (
        <>
          <TabPanel value={index} index={0} dir={theme.direction}>
            <ExparienceInformations
              setExparience={setExparience}
              exparience={exparience}
              isAdmin={isAdmin}
              files={files}
              setFiles={setFiles}
              entities={fetchedEntities}
              name={name}
              setName={setName}
              isNameSearching={isNameSearching}
              isNameUsed={isNameUsed}
              offerings={offerings}
            />
          </TabPanel>
          <TabPanel value={index} index={1} dir={theme.direction}>
            <Portal>
              <div style={{ position: "relative" }}>
                <Configurator
                  exparience={exparience}
                  setExparience={setExparience}
                  configObjects={configObjects}
                  noReloadImages={noReloadImages}
                  handleSaveExparience={handleSaveExparience}
                />
              </div>
            </Portal>
          </TabPanel>
          <TabPanel value={index} index={2} dir={theme.direction}>
            <Portal>
              <ExparienceWebVR
                exparience={exparience}
                setExparience={setExparience}
                isAdmin={isAdmin}
                frames={frames}
                setFrames={setFrames}
                visibleFrames={visibleFrames}
                setVisibleFrames={setVisibleFrames}
              />
              <Watermark
                exparience={exparience}
                setExparience={setExparience}
                isAdmin={isAdmin}
                files={files}
                setFiles={setFiles}
              />
            </Portal>
          </TabPanel>
          <TabPanel value={index} index={3} dir={theme.direction}>
            <div>
              <UsersManagement
                exparience={exparience}
                setExparience={setExparience}
                isAdmin={isAdmin}
              />
              <ExparienceSecurity
                exparience={exparience}
                setExparience={setExparience}
                setDatesDisable={setDatesDisable}
                isAdmin={isAdmin}
                handleSaveExparience={handleSaveExparience}
              />
            </div>
          </TabPanel>
          <TabPanel value={index} index={4} dir={theme.direction}>
            <Classes
              exparience={exparience}
              setExparience={setExparience}
              isAdmin={isAdmin}
            />
          </TabPanel>
          <TabPanel value={index} index={5} dir={theme.direction}>
            <Survey
              exparienceId={exparience?._id}
              frames={frames}
              setFrames={setFrames}
            />
          </TabPanel>
        </>
      ) : (
        <Container>
          <div className="loading-container">
            <CircularProgress size={50} />
            <Typography>{t("loaders.fetching")}</Typography>
          </div>
        </Container>
      )}
      {showStatsModal && (
        <ResponsiveDialogManager
          open={showStatsModal}
          onClose={handleCloseStatsModal}
          disableGutters
          fullScreen
          renderer={
            <MyExparienceStats
              exparience={exparience}
              {...props}
              closeModal={handleCloseStatsModal}
            />
          }
        />
      )}
    </Root>
  );
}
